<script lang="ts" setup>
import LightArrowIcon from "@/components/icon/LightArrowIcon.vue";
import ChatMessageItem from "@/components/mobile/ChatMessageItem.vue";
import MessageItem from "@/components/mobile/MessageItem.vue";
import { Popup } from "vant";
import { computed, ref, watch } from "vue";
import { useChat } from "@/stores/chat";
import { ChatMessage } from "@/types/entity";
import { useMate } from "@/stores/mate";

export interface SharedMessageItem {
  id: number;
  isCurrentUser: boolean;
  msg: string;
  mediaUrl: string;
  isSelected: boolean;
  name: string;
}

// Props
const props = withDefaults(
  defineProps<{
    show: boolean;
    selectedChatMessageList: SharedMessageItem[];
  }>(),
  {
    show: false,
  }
);
const emit = defineEmits<{
  (e: "update:show", show: boolean): void;
  (
    e: "update:selectedChatMessageList",
    selectedChatMessageList: SharedMessageItem[]
  ): void;
}>();
// State
const mateStore = useMate();
const chatStore = useChat();
const messageList = ref<SharedMessageItem[]>([]);
const selectedChatSessionId = ref<number | null>(null);
const isMsgListLoading = ref(false);
const selectedMsgIdSet = computed(() => {
  return new Set(
    messageList.value.filter((item) => item.isSelected).map((item) => item.id)
  );
});
// Lifecycle
watch(
  () => props.show,
  async (val) => {
    if (val) {
      if (props.selectedChatMessageList.length === 0) {
        selectedChatSessionId.value = null;
        return;
      }
      props.selectedChatMessageList.forEach((item) => {
        const msgItem = messageList.value.find((msgItem) => {
          return msgItem.id === item.id;
        });
        if (msgItem) {
          msgItem.isSelected = true;
        }
      });
    }
  }
);
// Event Handler
const onSessionSelection = async (sessionId: number) => {
  selectedChatSessionId.value = sessionId;
  isMsgListLoading.value = true;
  await chatStore.initMessageList(sessionId);
  const sessionMessageList: ChatMessage[] =
    chatStore.sessionId2MessageList[sessionId].messageList;
  if (sessionMessageList.length === 0) {
    messageList.value = [];
  }
  messageList.value = sessionMessageList
    .slice()
    .reverse()
    .map((item) => {
      return {
        id: item.id,
        isCurrentUser: mateStore.currentMate!.id === item.mate.id,
        msg: item.text,
        mediaUrl: item.mate.mediaUrl,
        isSelected: false,
      };
    });
  isMsgListLoading.value = false;
};
const onConfirmMessageSelection = () => {
  emit(
    "update:selectedChatMessageList",
    messageList.value.filter((item) => item.isSelected)
  );
  emit("update:show", false);
};
</script>
<template>
  <popup
    :show="show"
    class="h-[80%] overflow-hidden"
    position="bottom"
    round
    safe-area-inset-bottom
    @update:show="emit('update:show', $event)"
  >
    <!--  选择聊天session  -->
    <div
      v-if="!selectedChatSessionId"
      class="h-full overflow-hidden grid grid-rows-[auto_1fr] bg-base-100"
    >
      <div class="navbar h-top-nav border-b">
        <!-- 左 -->
        <div class="navbar-start h-full"></div>
        <!-- 中 -->
        <div class="navbar-center">
          <div class="font-bold">{{ $t("Chats.chooseChat") }}</div>
        </div>
        <!-- 右 -->
        <div class="navbar-end"></div>
      </div>
      <div class="overflow-y-auto">
        <div
          v-if="chatStore.filteredSessionList.length > 0"
          class="w-full flex flex-col"
        >
          <message-item
            v-for="item in chatStore.filteredSessionList"
            :key="item.id"
            :avatar-url="item.avatarUrl"
            :message="item.content"
            :name="item.title"
            :time="item.time"
            :type="2"
            :unread="item.unread"
            @click="onSessionSelection(item.id)"
          />
        </div>
        <div
          v-else
          class="w-full h-full flex flex-col items-center justify-center"
        >
          <div class="text-2xl font-bold">{{ $t("Message.noChatTitle") }}</div>
          <div class="text-neutral-400">{{ $t("Message.noChatDesc") }}</div>
        </div>
      </div>
    </div>
    <!--  选择聊天记录   -->
    <div v-else class="h-full overflow-hidden grid grid-rows-[auto_1fr]">
      <div class="navbar bg-base-100 h-top-nav border-b">
        <!-- 左 -->
        <div class="navbar-start h-full">
          <button class="btn btn-ghost" @click="selectedChatSessionId = null">
            <light-arrow-icon />
          </button>
        </div>
        <!-- 中 -->
        <div class="navbar-center">
          <div class="font-bold">{{ $t("Message.selectShareChat") }}</div>
        </div>
        <!-- 右 -->
        <div class="navbar-end">
          <button
            v-if="messageList.length > 0"
            :disabled="
              messageList.filter((item) => item.isSelected).length === 0
            "
            class="btn btn-primary btn-sm rounded"
            @click="onConfirmMessageSelection"
          >
            {{ $t("Buttons.done") }}
          </button>
        </div>
      </div>
      <div class="overflow-y-auto p-2 bg-base-100">
        <div
          v-if="isMsgListLoading"
          class="w-full h-full flex flex-col items-center justify-center"
        >
          <span class="loading"></span>
        </div>
        <div
          v-else-if="messageList.length === 0"
          class="w-full h-full flex flex-col items-center justify-center"
        >
          <div class="text-2xl font-bold">
            {{ $t("Message.noChatHistoryYet") }}
          </div>
          <div class="text-neutral-400">
            {{ $t("Message.noChatHistoryYetDesc") }}
          </div>
        </div>
        <div v-else class="flex flex-col">
          <div
            v-for="item in messageList"
            :key="item.id"
            class="w-full flex gap-3 items-end"
          >
            <div class="h-10 leading-10">
              <input
                v-model="item.isSelected"
                class="checkbox checkbox-sm rounded-full checked:checkbox-success"
                type="checkbox"
              />
            </div>
            <chat-message-item
              :avatar-url="item.mediaUrl"
              :msg="item.msg"
              :position="item.isCurrentUser ? 'end' : 'start'"
              class="flex-grow"
            />
          </div>
        </div>
      </div>
    </div>
  </popup>
</template>
